import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { ShieldCheck, User as UserIcon, Trash2, ChevronDown, ChevronUp, Lock, Plus, X, Pencil, Search, KeyRound } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const ROLES = ["customer", "staff", "admin", "super_admin"];
const BLANK = { email: "", name: "", password: "", role: "staff", active: true };

export default function AdminUsers() {
  const { user: me, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [permDefs, setPermDefs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => {
    load();
    api.get("/permissions").then((r) => setPermDefs(r.data.permissions)).catch(() => {});
  }, []);

  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); load(); toast.success("Role updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePerm = async (u, key) => {
    const current = new Set(u.permissions || []);
    if (current.has(key)) current.delete(key); else current.add(key);
    try {
      await api.patch(`/users/${u.id}`, { permissions: Array.from(current) });
      toast.success("Permissions updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createUser = async () => {
    if (!creating.email || !creating.password || !creating.name) {
      toast.error("Email, name, and password are required");
      return;
    }
    if (creating.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", creating);
      toast.success(`${creating.role} account created`);
      setCreating(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    if (!editing.name || !editing.email) {
      toast.error("Name and email are required");
      return;
    }
    if (editing.new_password && editing.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: editing.name, email: editing.email };
      if (editing.new_password) payload.new_password = editing.new_password;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success(editing.new_password ? "Profile updated & password reset" : "Profile updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const availableRoles = ROLES.filter((r) => r !== "super_admin" || isSuperAdmin);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });
  }, [users, search, roleFilter]);

  const counts = useMemo(() => {
    const out = { all: users.length };
    for (const r of ROLES) out[r] = users.filter((u) => u.role === r).length;
    return out;
  }, [users]);

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">Users</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">User management</h1>
        </div>
        <button onClick={() => setCreating({ ...BLANK })}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="new-user-btn">
          <Plus size={16} /> New user
        </button>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Super Admins always have access to everything. Expand a row to toggle which sections that user can access.
        {!isSuperAdmin && " Admins cannot modify Super Admin accounts."}
      </p>

      {/* Search + role filter */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-200 rounded-lg"
            data-testid="users-search"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          <FilterPill active={!roleFilter} onClick={() => setRoleFilter("")}>
            All · {counts.all}
          </FilterPill>
          {ROLES.map((r) => (
            <FilterPill key={r} active={roleFilter === r} onClick={() => setRoleFilter(r)} testid={`filter-role-${r}`}>
              {r} · {counts[r] || 0}
            </FilterPill>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-zinc-50 text-zinc-500 text-xs">
            <tr>
              <th className="text-left px-4 py-3 w-8"></th>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Active</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => {
              const canModify = !(u.role === "super_admin" && !isSuperAdmin);
              const protectedRow = u.role === "super_admin";
              const isOpen = expanded === u.id;
              const showPerms = u.role === "admin" || u.role === "super_admin" || u.role === "staff";
              return (
                <React.Fragment key={u.id}>
                  <tr className="border-t border-zinc-100" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      {showPerms && (
                        <button onClick={() => setExpanded(isOpen ? null : u.id)} className="p-1 hover:bg-zinc-100 rounded" data-testid={`expand-user-${u.id}`}>
                          {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 flex items-center gap-2">
                      {u.role === "super_admin" ? <ShieldCheck size={14} style={{ color: "#F74B64" }} />
                        : u.role === "admin" ? <UserIcon size={14} style={{ color: "#FFA62B" }} />
                        : u.role === "staff" ? <UserIcon size={14} style={{ color: "#3B82F6" }} />
                        : <UserIcon size={14} className="text-zinc-400" />}
                      {u.name}
                    </td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3">
                      <select disabled={!canModify || u.id === me?.id} value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                              className="border border-zinc-200 rounded px-2 py-1 text-xs disabled:opacity-50" data-testid={`role-${u.id}`}>
                        {ROLES.map((r) => <option key={r} value={r} disabled={r === "super_admin" && !isSuperAdmin}>{r}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <label className="inline-flex items-center gap-1 text-xs">
                        <input type="checkbox" disabled={!canModify || u.id === me?.id} checked={u.active} onChange={() => toggleActive(u)} />
                        {u.active ? "Active" : "Disabled"}
                      </label>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button disabled={!canModify}
                              onClick={() => setEditing({ id: u.id, name: u.name, email: u.email, new_password: "" })}
                              className="p-1.5 hover:bg-zinc-100 rounded disabled:opacity-30 disabled:hover:bg-transparent mr-1"
                              title="Edit name, email or reset password"
                              data-testid={`edit-user-${u.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button disabled={protectedRow || u.id === me?.id} onClick={() => remove(u)}
                              className="p-1.5 hover:bg-red-50 text-red-500 rounded disabled:opacity-30 disabled:hover:bg-transparent"
                              title={protectedRow ? "Super Admin cannot be deleted" : "Delete"}
                              data-testid={`delete-user-${u.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                  {isOpen && showPerms && (
                    <tr className="bg-zinc-50 border-t border-zinc-100">
                      <td colSpan={6} className="px-6 py-5">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="font-display font-bold text-sm">Backend access checklist</div>
                          {u.role === "super_admin" && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                              <Lock size={10} /> Super Admin — full access always
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {permDefs.map((p) => {
                            const granted = u.role === "super_admin"
                              ? true
                              : (u.permissions || []).includes(p.key);
                            const disabled = u.role === "super_admin" || !canModify || u.id === me?.id;
                            return (
                              <label key={p.key} className={`flex items-center gap-2 text-sm p-3 rounded-lg border ${granted ? "border-[#F74B64] bg-white" : "border-zinc-200 bg-white"}`}>
                                <input type="checkbox" checked={granted} disabled={disabled}
                                       onChange={() => togglePerm(u, p.key)}
                                       data-testid={`perm-${u.id}-${p.key}`} />
                                <div className="flex-1">
                                  <div className="font-semibold">{p.label}</div>
                                  <div className="text-[10px] text-zinc-500 font-mono">{p.key}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 sm:p-6"
             onClick={() => setCreating(null)} data-testid="new-user-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg sm:text-xl">New user</div>
              <button onClick={() => setCreating(null)} className="p-1 hover:bg-zinc-100 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <Field label="Name">
                <input className="input" value={creating.name}
                       onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                       data-testid="new-user-name" />
              </Field>
              <Field label="Email">
                <input className="input" type="email" value={creating.email}
                       onChange={(e) => setCreating({ ...creating, email: e.target.value.toLowerCase() })}
                       data-testid="new-user-email" />
              </Field>
              <Field label="Temporary password (min 6)">
                <input className="input" type="text" value={creating.password}
                       onChange={(e) => setCreating({ ...creating, password: e.target.value })}
                       placeholder="They can change it later from their profile"
                       data-testid="new-user-password" />
              </Field>
              <Field label="Role">
                <select className="input" value={creating.role}
                        onChange={(e) => setCreating({ ...creating, role: e.target.value })}
                        data-testid="new-user-role">
                  {availableRoles.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <p className="text-[11px] text-zinc-500 mt-1">
                  {creating.role === "customer" && "Public website account — can shop, save boxes, leave reviews."}
                  {creating.role === "staff" && "Backend access defaults to Orders, Scanner, and Inventory. Adjust below or after creating."}
                  {creating.role === "admin" && "Full backend access (cannot modify Super Admin accounts)."}
                  {creating.role === "super_admin" && "Top-level — irrevocable backend access. Can create other Super Admins."}
                </p>
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={creating.active}
                       onChange={(e) => setCreating({ ...creating, active: e.target.checked })} />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setCreating(null)} className="btn-ghost">Cancel</button>
              <button onClick={createUser} disabled={saving}
                      className="btn-primary disabled:opacity-50" data-testid="save-new-user-btn">
                {saving ? "Creating…" : "Create user"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 sm:p-6"
             onClick={() => setEditing(null)} data-testid="edit-user-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg sm:text-xl">Edit user</div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-zinc-100 rounded">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <Field label="Name">
                <input className="input" value={editing.name}
                       onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                       data-testid="edit-user-name" />
              </Field>
              <Field label="Email">
                <input className="input" type="email" value={editing.email}
                       onChange={(e) => setEditing({ ...editing, email: e.target.value.toLowerCase() })}
                       data-testid="edit-user-email" />
              </Field>
              <div className="pt-2 border-t border-zinc-100">
                <div className="flex items-center gap-2 mb-2">
                  <KeyRound size={14} className="text-zinc-500" />
                  <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                    Reset password
                  </div>
                </div>
                <p className="text-[11px] text-zinc-500 mb-2">
                  Leave blank to keep the current password. If set, the user's existing password is
                  replaced immediately and they'll need to use the new one on next sign in.
                </p>
                <input className="input" type="text"
                       placeholder="New password (min 6) — leave blank to skip"
                       value={editing.new_password}
                       onChange={(e) => setEditing({ ...editing, new_password: e.target.value })}
                       data-testid="edit-user-password" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
              <button onClick={saveEdit} disabled={saving}
                      className="btn-primary disabled:opacity-50" data-testid="save-edit-user-btn">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.input { width:100%; border:1px solid #e4e4e7; border-radius:0.5rem; padding:0.5rem 0.75rem; font-size:0.875rem; }`}</style>
    </div>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
              active
                ? "bg-[#F74B64] text-white border-[#F74B64]"
                : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
            }`}
            data-testid={testid}>
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-600 block mb-1">{label}</label>
      {children}
    </div>
  );
}
